import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  IActivo,
  ICategoriaActivo,
  IConfigReenvio,
  ICliente,
  ICreateConfigReenvio,
  IQueryParam,
  IUpdateConfigReenvio,
  IAgrupacionReenvio,
  ITracker,
  IDispositivoAlarma,
  IReglaReenvio,
  Protocolo,
  IPopulate,
  MetodoReenvio,
  FormatosMensajeComunicador,
} from 'modelos/src';
import { firstValueFrom } from 'rxjs';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { ConfigReenviosService } from '../../../../../auxiliares/servicios/http/config-reenvios.service';
import { MatDialogRef } from '@angular/material/dialog';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import { LoginService } from '../../../../login/login.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { DateRange } from '../../../../../auxiliares/date-range-selector/date-range-selector.component';
import {
  ITestSoflexData,
  TestSoflexComponent,
} from '../test-soflex/test-soflex.component';
import { ModeloDispositivoSelectorCacheService } from '../../../../../componentes/selectores/modelo-dispositivo-selector/modelo-dispositivo-selector-cache.service';
import { ActivosService } from '../../../../../auxiliares/servicios/http/activos.service';

@Component({
  selector: 'app-crear-editar-config-reenvio',
  templateUrl: './crear-editar-config-reenvio.component.html',
  styleUrl: './crear-editar-config-reenvio.component.scss',
  standalone: false,
})
export class CrearEditarConfigReenviosComponent implements OnInit {
  dialogRef = inject<MatDialogRef<CrearEditarConfigReenviosComponent>>(
    MatDialogRef,
    { optional: true },
  );
  private route = inject(ActivatedRoute);
  private lista2 = inject(Lista2Service);
  private service = inject(ConfigReenviosService);
  helper = inject(HelperService);
  private dialogService = inject(DialogService);
  private formularios = inject(FormularioDialogService);
  private modeloCache = inject(ModeloDispositivoSelectorCacheService);
  private activosService = inject(ActivosService);

  public clientePropio = LoginService.getCliente();
  public loading = false;
  public id: string | null = null;
  public title?: string;
  public subtitle?: string;
  public configreenvio?: IConfigReenvio;

  public trackers?: ITracker[];
  public alarmas?: IDispositivoAlarma[];
  public vehiculos?: IActivo[];

  public form?: FormGroup;
  public range: DateRange = {};

  /// Vehículo: la config se guarda con el _id del vehículo y se aplica al
  /// tracker que tenga asignado en cada reporte (si le cambian el tracker, el
  /// reenvío pasa al nuevo sin tocar la config).
  public tiposEntidades = ['Tracker', 'Vehículo', 'Alarma'];
  // Opciones fijas de los selects de protocolo
  public readonly protocolos = [
    { valor: 'TCP', etiqueta: 'TCP' },
    { valor: 'UDP', etiqueta: 'UDP' },
  ];
  public agrupacionesReenvio: IAgrupacionReenvio[] = [
    'Entidad',
    'Todos los trackers del cliente',
    'Todas las alarmas del cliente',
  ];
  public metodosReenvio: MetodoReenvio[] = [
    'Básico',
    'Seguridad Evento Externo',
    'Caessat',
    'Cersat',
    'Iron Tracking',
    'Logictracker',
    'Objetivo AVL',
  ];

  // Opciones hardcodeadas de puertos/protocolos con nombres
  public puertosProtocolosTrackers: {
    puerto: number;
    protocolo: Protocolo;
    nombre: string;
  }[] = [
    { puerto: 5031, protocolo: 'TCP', nombre: 'Trax' },
    { puerto: 5031, protocolo: 'UDP', nombre: 'Trax' },
    { puerto: 5023, protocolo: 'TCP', nombre: 'GTO6' },
    { puerto: 5023, protocolo: 'UDP', nombre: 'GTO6' },
    { puerto: 5001, protocolo: 'TCP', nombre: 'GPS103' },
    { puerto: 5001, protocolo: 'UDP', nombre: 'GPS103' },
  ];

  public puertosProtocolosAlarmas: {
    puerto: number;
    protocolo: Protocolo;
    nombre: string;
  }[] = [
    { puerto: 6010, protocolo: 'UDP', nombre: 'Avatis' },
    { puerto: 6030, protocolo: 'TCP', nombre: 'Dahua' },
    { puerto: 7000, protocolo: 'TCP', nombre: 'Lantrix' },
    { puerto: 7000, protocolo: 'UDP', nombre: 'Lantrix' },
    { puerto: 6000, protocolo: 'UDP', nombre: 'Nanocom' },
    { puerto: 6000, protocolo: 'TCP', nombre: 'Netio' },
    { puerto: 6021, protocolo: 'UDP', nombre: 'Garnet' },
    { puerto: 6020, protocolo: 'UDP', nombre: 'Garnet' },
    { puerto: 9065, protocolo: 'TCP', nombre: 'Alarcom' },
    { puerto: 6040, protocolo: 'TCP', nombre: 'Hikvision' },
  ];

  get puertosProtocolosActuales(): {
    puerto: number;
    protocolo: Protocolo;
    nombre: string;
  }[] {
    const agrupacion = this.form?.get('agrupacionReenvio')?.value;
    const tipoEntidad = this.form?.get('tipoEntidad')?.value;
    const idEntidad = this.form?.get('idEntidadReenvio')?.value;

    // Determinar si es tracker o alarma
    const esTracker =
      agrupacion === 'Todos los trackers del cliente' ||
      (agrupacion === 'Entidad' &&
        (tipoEntidad === 'Tracker' || tipoEntidad === 'Vehículo'));

    let opciones = esTracker
      ? this.puertosProtocolosTrackers
      : this.puertosProtocolosAlarmas;

    // Aplicar filtro si es una entidad alarma específica
    if (
      agrupacion === 'Entidad' &&
      tipoEntidad === 'Alarma' &&
      idEntidad &&
      !esTracker
    ) {
      const alarmaSeleccionada = this.alarmas?.find((a) => a._id === idEntidad);

      // Un formato por cada comunicador (protocolos distintos, ej. principal
      // + backup); si no hay comunicadores, el formato lo aporta el modelo
      // del panel. Unión de las opciones que aplicarían para cada uno.
      const formatos = alarmaSeleccionada?.comunicadores?.length
        ? alarmaSeleccionada.comunicadores
            .map(
              (c) =>
                this.modeloCache.getModeloPorIdSync(c.idComunicador)
                  ?.formatoMensaje,
            )
            .filter((f): f is FormatosMensajeComunicador => !!f)
        : [alarmaSeleccionada?.modelo?.formatoMensaje].filter(
            (f): f is FormatosMensajeComunicador => !!f,
          );

      if (formatos.length) {
        const resultados = formatos.map((f) =>
          this.filtrarOpcionesPorFormato(opciones, f),
        );
        const nombresPuertos = new Set(
          resultados.flat().map((p) => `${p.puerto}-${p.protocolo}`),
        );
        opciones = opciones.filter((p) =>
          nombresPuertos.has(`${p.puerto}-${p.protocolo}`),
        );
      } else {
        opciones = this.filtrarOpcionesPorFormato(opciones, undefined);
      }
    }

    return opciones;
  }

  /** Mismo criterio que antes, ahora reusable por comunicador individual. */
  private filtrarOpcionesPorFormato(
    opciones: { puerto: number; protocolo: Protocolo; nombre: string }[],
    formatoMensaje: string | undefined,
  ) {
    if (!formatoMensaje) {
      // Sin formato: mostrar todos menos Dahua, Hikvision y Garnet.
      return opciones.filter(
        (p) => !['Dahua', 'Hikvision', 'Garnet'].includes(p.nombre),
      );
    }
    const fm = formatoMensaje.toLowerCase();
    if (fm.includes('hikvision')) {
      return opciones.filter((p) => p.nombre === 'Hikvision');
    }
    if (fm.includes('garnet') || fm.includes('garnet titanium')) {
      return opciones.filter((p) => p.nombre === 'Garnet');
    }
    if (fm.includes('dahua')) {
      return opciones.filter((p) => p.nombre === 'Dahua');
    }
    // Otro tipo: mostrar todos menos Dahua, Hikvision y Garnet.
    return opciones.filter(
      (p) => !['Dahua', 'Hikvision', 'Garnet'].includes(p.nombre),
    );
  }

  get opcionesReenvio() {
    return this.form?.get('opcionesReenvio') as FormGroup;
  }

  get opcionesSoflex() {
    return this.opcionesReenvio?.get('opcionesSoflex') as FormGroup;
  }

  get opcionesObjetivoAVL() {
    return this.opcionesReenvio?.get('opcionesObjetivoAVL') as FormGroup;
  }

  get reglasReenvio() {
    return this.opcionesReenvio?.get('reglasReenvio') as FormArray;
  }

  private async listar() {
    if (this.id) {
      this.configreenvio = await firstValueFrom(
        this.lista2.configReenvio(this.id!),
      );
    }
  }
  private async listarTrackers() {
    const query: IQueryParam = {
      sort: 'nombre',
      select: 'nombre',
      includeChildren: true,
      unlimited: true,
    };
    this.trackers = (await firstValueFrom(this.lista2.trackers(query))).datos;
  }
  /// Directo al service y no por lista2.vehiculos, que deja afuera a los
  /// colectivos.
  private async listarVehiculos() {
    const categoria: ICategoriaActivo = 'Vehículo';
    const query: IQueryParam = {
      filter: JSON.stringify({ categoria }),
      sort: 'identificacion',
      select: 'identificacion vehiculo.patente idTracker',
      includeChildren: true,
      unlimited: true,
    };
    this.vehiculos = (await this.activosService.getFiltered(query)).datos;
  }
  private async listarAlarmas() {
    // El formato de cada comunicador se resuelve por idComunicador contra el
    // cache de modelos (getModeloPorIdSync) — el comunicador es opcional; si
    // no hay ninguno, el formato lo aporta el modelo del panel.
    const populate: IPopulate = [{ path: 'modelo', select: 'formatoMensaje' }];
    const query: IQueryParam = {
      sort: 'nombre',
      select: 'nombre comunicadores idModelo modelo',
      includeChildren: true,
      unlimited: true,
      populate: JSON.stringify(populate),
    };
    this.alarmas = (await firstValueFrom(this.lista2.alarmas(query))).datos;
  }

  private createForm() {
    // Crear FormArray para reglas de reenvio
    const reglasFormArray = new FormArray(
      (this.configreenvio?.opcionesReenvio?.reglasReenvio || []).map(
        (regla) =>
          new FormGroup({
            puertoEntrada: new FormControl(regla.puertoEntrada),
            protocoloEntrada: new FormControl(regla.protocoloEntrada),
            hostSalida: new FormControl(regla.hostSalida),
            puertoSalida: new FormControl(regla.puertoSalida),
            protocoloSalida: new FormControl(regla.protocoloSalida),
          }),
      ),
    );

    this.form = new FormGroup(
      {
        // Se puede crear una config apagada y activarla después: mientras
        // `activo` sea false no se reenvía nada, sin borrar la configuración.
        activo: new FormControl(this.configreenvio?.activo ?? true),
        agrupacionReenvio: new FormControl(
          this.configreenvio?.agrupacionReenvio,
          Validators.required,
        ),
        idClienteReenvio: new FormControl(this.configreenvio?.idClienteReenvio),
        idEntidadReenvio: new FormControl(this.configreenvio?.idEntidadReenvio),
        reenviarHijos: new FormControl(
          this.configreenvio?.reenviarHijos || false,
        ),
        opcionesReenvio: new FormGroup({
          metodo: new FormControl(
            this.configreenvio?.opcionesReenvio?.metodo || 'Básico',
          ),
          usaReglaReenvio: new FormControl(
            this.configreenvio?.opcionesReenvio?.usaReglaReenvio || false,
          ),
          host: new FormControl(this.configreenvio?.opcionesReenvio?.host),
          puerto: new FormControl(this.configreenvio?.opcionesReenvio?.puerto),
          apikey: new FormControl(this.configreenvio?.opcionesReenvio?.apikey),
          protocolo: new FormControl(
            this.configreenvio?.opcionesReenvio?.protocolo,
          ),
          usuario: new FormControl(
            this.configreenvio?.opcionesReenvio?.usuario,
          ),
          contrasena: new FormControl(
            this.configreenvio?.opcionesReenvio?.contrasena,
          ),
          reglasReenvio: reglasFormArray,
          opcionesSoflex: new FormGroup({
            providerid: new FormControl(
              this.configreenvio?.opcionesReenvio?.opcionesSoflex?.providerid,
            ),
            version: new FormControl(
              this.configreenvio?.opcionesReenvio?.opcionesSoflex?.version,
            ),
          }),
          // La URL y el token no se cargan acá: son del prestador y viven en
          // cliente.config.integracionObjetivosAVL.
          opcionesObjetivoAVL: new FormGroup({
            deviceid: new FormControl(
              this.configreenvio?.opcionesReenvio?.opcionesObjetivoAVL
                ?.deviceid,
            ),
            nroCarta: new FormControl(
              this.configreenvio?.opcionesReenvio?.opcionesObjetivoAVL
                ?.nroCarta,
            ),
          }),
        }),
        // Solo para el form
        tipoEntidad: new FormControl(this.tipoEntidadInicial()),
        periodoInicio: new FormControl(
          this.configreenvio?.periodoInicio || null,
        ),
        periodoFin: new FormControl(this.configreenvio?.periodoFin || null),
      },
      // { validators: this.clientesValidator() },
    );

    // Inicializar range para el selector de periodo
    this.range = {
      start: this.configreenvio?.periodoInicio,
      end: this.configreenvio?.periodoFin,
    };

    // Desseleccionar reenviarHijos cuando se selecciona agrupación por Entidad
    this.form.get('agrupacionReenvio')?.valueChanges.subscribe((value) => {
      if (value === 'Entidad') {
        this.form?.get('reenviarHijos')?.setValue(false);
      }
      // Regenerar reglas si está activado el toggle
      if (this.opcionesReenvio?.get('usaReglaReenvio')?.value) {
        this.generarReglasAutomaticamente();
      }
    });

    // Regenerar reglas cuando cambia el tipo de entidad
    this.form.get('tipoEntidad')?.valueChanges.subscribe(() => {
      if (this.opcionesReenvio?.get('usaReglaReenvio')?.value) {
        this.generarReglasAutomaticamente();
      }
    });

    // Regenerar reglas cuando cambia la entidad seleccionada (alarma específica)
    this.form.get('idEntidadReenvio')?.valueChanges.subscribe(() => {
      if (this.opcionesReenvio?.get('usaReglaReenvio')?.value) {
        this.generarReglasAutomaticamente();
      }
    });

    // Generar o limpiar reglas cuando cambia el toggle
    this.opcionesReenvio
      .get('usaReglaReenvio')
      ?.valueChanges.subscribe((value) => {
        if (value) {
          this.generarReglasAutomaticamente();
        } else {
          this.reglasReenvio.clear();
        }
      });
  }

  /// La config no guarda el tipo: sale de qué populate vino (tracker o
  /// alarma) o, para un vehículo, de encontrar el id en la lista.
  private tipoEntidadInicial(): string {
    if (this.configreenvio?.tracker) return 'Tracker';
    if (this.configreenvio?.dispositivoAlarma) return 'Alarma';
    if (this.vehiculoDeLaConfig()) return 'Vehículo';
    return 'Alarma';
  }

  /// Por identificación o por patente.
  public buscarVehiculo = (termino: string, vehiculo: IActivo): boolean => {
    const t = termino.toLowerCase();
    return [vehiculo.identificacion, vehiculo.vehiculo?.patente].some((v) =>
      v?.toLowerCase().includes(t),
    );
  };

  private vehiculoDeLaConfig(): IActivo | undefined {
    const id = this.configreenvio?.idEntidadReenvio;
    return id ? this.vehiculos?.find((v) => v._id === id) : undefined;
  }

  private generarReglasAutomaticamente() {
    // Limpiar reglas existentes
    this.reglasReenvio.clear();

    // Crear una regla por cada puerto/protocolo disponible
    this.puertosProtocolosActuales.forEach((pp) => {
      const nuevaRegla = new FormGroup({
        puertoEntrada: new FormControl(pp.puerto),
        protocoloEntrada: new FormControl(pp.protocolo),
        hostSalida: new FormControl(null, Validators.required),
        puertoSalida: new FormControl(null, [
          Validators.required,
          Validators.pattern(/^\d+$/),
        ]),
        protocoloSalida: new FormControl(null, Validators.required),
      });
      this.reglasReenvio.push(nuevaRegla);
    });
  }

  private getData() {
    const data: ICreateConfigReenvio | IUpdateConfigReenvio = this.form?.value;
    if (data?.opcionesReenvio?.metodo === 'Logictracker') {
      data.opcionesReenvio.protocolo = null;
    }
    return data;
  }

  public onRangeChange(range: DateRange) {
    this.range = range;
    this.form?.patchValue({
      periodoInicio: range.start || null,
      periodoFin: range.end || null,
    });
  }

  public volver() {
    this.helper.volver();
  }

  public getNombrePuerto(puerto: number, protocolo: Protocolo): string {
    const pp = this.puertosProtocolosActuales.find(
      (p) => p.puerto === puerto && p.protocolo === protocolo,
    );
    return pp?.nombre || '';
  }

  public async onSubmit() {
    const res = await this.dialogService.confirm(
      this.title,
      this.id
        ? '¿Querés guardar los cambios de la configuración de reenvío?'
        : '¿Querés crear la configuración de reenvío?',
    );
    if (!res) return;
    try {
      if (this.id) {
        // Update
        const update = this.getData() as IUpdateConfigReenvio;
        if (!update) return;
        await this.service.update(this.id, update);
        this.helper.notifSuccess('Configuración de reenvío editada');
      } else {
        // Create
        const create = this.getData() as ICreateConfigReenvio;
        if (!create) return;
        await this.service.create(create);
        this.helper.notifSuccess('Configuración de reenvío creada');
      }
      this.helper.volver();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  /// Test de la config Soflex del formulario (guardada o no): informa qué
  /// vehículos se reenviarían y qué falta para crearlos en Soflex.
  public probarConfigSoflex() {
    const config: IConfigReenvio = {
      ...(this.form?.value as IConfigReenvio),
      _id: this.id || undefined,
      idCliente: this.configreenvio?.idCliente,
    };
    this.formularios.abrir<TestSoflexComponent, ITestSoflexData>(
      TestSoflexComponent,
      { tamano: 'grande', data: { config }, cerrarAfuera: true },
    );
  }

  private getTipoIntegracion() {
    /// El cliente tiene que tener activado el modulo de soflex para poder crear las integraciones
    if (this.clientePropio?.config?.integracionSoflex?.activo) {
      this.metodosReenvio.push('Soflex');
    }
  }

  private setTitles() {
    if (this.configreenvio) {
      this.title = 'Editar Configuración de Reenvío';
      this.subtitle =
        this.configreenvio?.cliente?.nombre ||
        this.configreenvio?.tracker?.nombre ||
        this.configreenvio?.dispositivoAlarma?.nombre ||
        this.vehiculoDeLaConfig()?.identificacion ||
        this.configreenvio?.agrupacionReenvio;
    } else {
      this.title = 'Crear Configuración de Reenvío';
    }
  }

  // Hooks
  async ngOnInit() {
    this.loading = true;
    // Warm-up: puertosProtocolosActuales resuelve el formato de cada
    // comunicador por id de forma síncrona (getModeloPorIdSync).
    this.modeloCache.getModelos().subscribe();
    const params = await firstValueFrom(this.route.paramMap);
    this.id = params.get('id');
    await Promise.all([
      this.listar(),
      this.listarTrackers(),
      this.listarAlarmas(),
      this.listarVehiculos(),
    ]);
    this.getTipoIntegracion();
    this.setTitles();
    this.createForm();
    this.loading = false;
  }
}
